import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SocialAuthService,
} from '@abacritt/angularx-social-login';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';
import {
  SocialAccountLoginDetails,
  UserRegistrationDetails,
} from 'src/app/api/types/User';
import {
  SocialLogin,
  SocialLoginFailed,
} from 'src/app/auth/+state/auth.actions';
import { AuthState } from 'src/app/auth/+state/auth.reducer';
import { LogService } from 'src/app/shared/logging/LogService';
import {
  environment as envConst,
  environment,
} from 'src/environments/environment';
import { MessageService } from '../message.service';

import { HelperService } from 'src/app/public/helper.service';
import { Device, DeviceInfo } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { WhitelabelService } from '../whitelabel.service';
import { NavigationEnd, Router } from '@angular/router';
import { StoreLanguageService } from 'src/app/public/store-language.service';

declare var AppleID: any;

type SocialProvider = 'google' | 'facebook' | 'apple';

@Component({
  selector: 'app-user-login',
  templateUrl: './user-login.component.html',
  styleUrls: ['./user-login.component.scss'],
})
export class UserLoginComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Marks the document as the checkout's embedded login iframe. */
  static readonly EMBEDDED_LOGIN_CLASS = 'embedded-login';

  /**
   * Whether *this* instance put `EMBEDDED_LOGIN_CLASS` on `<html>`. The class is
   * global document state, so the teardown is keyed on what was actually
   * applied rather than on re-evaluating the condition.
   */
  private embeddedLoginApplied = false;
  private readonly destroy$ = new Subject<void>();
  jwtHelper = new JwtHelperService();
  @Input() authType: string;
  /**
   * Which translation keys the Google / Facebook / Apple labels use: the admin
   * login and registration keep `admin.login.social.*`, the customer sign-in
   * card uses `public.login.social.*`, because the storefront loads only the
   * customer translations (#11850).
   */
  @Input() labelScope: 'admin' | 'customer' = 'admin';
  /**
   * The store language the customer sign-in card hands to this page when it
   * embeds it as the web iframe (`?mode=customer-login&lang=<locale>`). Also
   * the language Google renders its own button in. Empty everywhere else.
   */
  labelLang = '';
  /**
   * The labels in `labelLang`, from its customer translations, once loaded.
   * Until then, or if they cannot be loaded, the labels come from `labelScope`
   * in the page's own language, as before.
   */
  langLabels: Partial<Record<SocialProvider, string>> = {};
  stacked = false;
  @Output() submitEvent = new EventEmitter<UserRegistrationDetails>();
  eventsSubject: Subject<any> = new Subject<any>();
  isInvitationUser = false;
  iconViewBreakpoint = 768;
  sideSpacing = 57;
  iconMode = false;
  googleRendered = false;
  mode: string;
  isAndroidApp = false;
  socialLoginEnabled;
  /**
   * Card #9881: the PayDroid build of GoAdmin, on a PAX terminal, shows no
   * provider button at all. Google's sign-in needs Google Mobile Services and
   * Facebook's the Facebook app or a Custom Tabs browser, none of which a PAX
   * terminal has, so either button could only fail.
   *
   * Its own gate, on the whole panel, rather than folded into
   * `socialLoginEnabled`: the Facebook button of the mobile apps has never
   * looked at that flag, and changing that for the other apps is not this
   * card's to do. The login and registration pages already leave this
   * component out on PayDroid; this covers every other page that embeds it,
   * such as the storefront checkout's customer sign-in.
   */
  payDroidBuild: boolean;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private translate: TranslateService,
    private authService: SocialAuthService,
    private logger: LogService,
    private store: Store<AuthState>,
    private el: ElementRef,
    private messageService: MessageService,
    public helper: HelperService,
    public whiteLabelService: WhitelabelService,
    private router: Router,
    private storeLanguageService: StoreLanguageService,
  ) {
    const urlParams = new URLSearchParams(window.location.search);
    this.mode = urlParams.get('mode');
    this.stacked = !(this.mode == 'customer-login');
    if (this.mode === 'customer-login') {
      const lang = urlParams.get('lang')?.toLowerCase();
      this.labelLang = lang && /^[a-z]{2,3}$/.test(lang) ? lang : '';
    }
    this.socialLoginEnabled = whiteLabelService.getSocialLoginEnabled();
    this.payDroidBuild = helper.isPayDroidBuild();
  }
  async ngOnInit() {
    if (this.isEmbeddedCustomerLogin()) {
      // Embedded in the checkout's login iframe: make this document's canvas
      // transparent so the store background shows through (see
      // html.embedded-login in styles.scss). Removed again in ngOnDestroy so
      // the class can never outlive the component.
      this.document.documentElement.classList.add(
        UserLoginComponent.EMBEDDED_LOGIN_CLASS,
      );
      this.embeddedLoginApplied = true;
    }
    this.isPlatformAndroid();
    this.document.documentElement.lang = 'en';
    this.loadLangLabels();
    if (this.mode && this.mode.includes('invite')) {
      this.isInvitationUser = true;
    }
    if (!this.authType) {
      window.addEventListener(
        'message',
        (event) => {
          const { data } = event;
          if (data.logout !== null && data.logout !== undefined) {
            if (data.logout === true) {
              this.logout();
            }
          }
        },
        false,
      );
    }

    this.authService.authState.pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        if (user?.provider === GoogleLoginProvider.PROVIDER_ID) {
          user.authToken = user.idToken;
          window.parent.postMessage(
            { ...user, mode: this.mode },
            document.referrer,
          );
        }
      },
      error: (err) => {
        console.error('Authentication subscription error', err);
      },
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.messageService.sendMessage({
          type: 'user-login-url-changed',
          url: event.url,
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Remove only what this instance added. Re-deriving the condition here
    // instead would let an instance that never set the class strip it from
    // <html> on destroy — and it would read `window.self !== window.top` a
    // second time, so the add and the remove could disagree.
    if (!this.embeddedLoginApplied) {
      return;
    }
    this.document.documentElement.classList.remove(
      UserLoginComponent.EMBEDDED_LOGIN_CLASS,
    );
    this.embeddedLoginApplied = false;
  }

  /**
   * True only for the customer-login mode rendered inside the checkout's
   * iframe. The mode alone is a URL parameter, so the frame context is checked
   * too: the transparent canvas must never apply to the main SPA document.
   */
  isEmbeddedCustomerLogin(): boolean {
    return this.mode == 'customer-login' && this.isInsideFrame();
  }

  /** Whether this document is rendered inside another window (an iframe). */
  isInsideFrame(): boolean {
    return window.self !== window.top;
  }

  async ngAfterViewInit(): Promise<void> {
    const deviceInfo = await Device.getInfo();
    if ((deviceInfo as unknown as DeviceInfo).platform === 'web') {
      setTimeout(() => this.checkIconMode(), 10);
      setTimeout(() => this.initGoogle(), 50);
    }
  }
  private async isPlatformAndroid(): Promise<boolean> {
    const deviceInfo = await Capacitor.getPlatform();
    if (deviceInfo === 'android') {
      this.isAndroidApp = true;
    }
    return this.isAndroidApp;
  }

  initGoogle(): void {
    if (this.googleRendered) {
      return;
    }
    this.renderGoogleButton();
    setTimeout(() => this.initGoogle(), 50);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const deviceInfo = Device.getInfo();

    if ((deviceInfo as unknown as DeviceInfo).platform === 'web') {
      this.checkIconMode();
      this.renderGoogleButton();
    }
  }

  renderGoogleButton() {
    const element = document.getElementById('google-btn-tab');
    const width = element.offsetWidth + (this.stacked ? 0 : -5);
    this.eventsSubject.next([width, this.iconMode]);
  }

  markRendered() {
    this.googleRendered = true;
  }

  socialKey(provider: SocialProvider, scope = this.labelScope): string {
    return `${scope === 'customer' ? 'public' : 'admin'}.login.social.${provider}`;
  }

  /**
   * Resolves the three labels in the store language handed over in the URL.
   * The page's own current language cannot be used for that: it is decided by
   * two loads racing at start-up (the browser language in `AppModule`, and
   * `StoreLanguageService`), and neither of them is given the store language.
   */
  private loadLangLabels(): void {
    if (!this.labelLang) {
      return;
    }
    this.storeLanguageService
      .loadCustomerTranslations(this.labelLang)
      .then((translations) => {
        const providers: SocialProvider[] = ['google', 'facebook', 'apple'];
        providers.forEach((provider) => {
          const key = this.socialKey(provider, 'customer');
          const label = this.translate.getParsedResult(translations, key);
          // A key missing from the file comes back as the key itself.
          if (typeof label === 'string' && label && label !== key) {
            this.langLabels[provider] = label;
          }
        });
      })
      .catch(() => {
        // No customer translations for this language: keep the page's own.
      });
  }

  googleText() {
    return this.mode?.includes('registration') ? 'signup_with' : 'signin_with';
  }

  checkIconMode() {
    this.iconMode =
      !this.stacked &&
      this.el.nativeElement.offsetWidth > 0 &&
      this.el.nativeElement.offsetWidth <
        this.iconViewBreakpoint - this.sideSpacing;
  }

  facebookLoginAction() {
    const provider = FacebookLoginProvider.PROVIDER_ID;
    this.authService
      .signIn(provider)
      .then((user) => {
        user.provider = FacebookLoginProvider.PROVIDER_ID;
        window.parent.postMessage(
          { ...user, mode: this.mode },
          document.referrer,
        );
      })
      .catch((e) => {
        this.logger.error(
          `onSocialLogin error during sign-in with provider ${provider}`,
          e,
        );
        if (this.authType === 'login') {
          this.store.dispatch(
            new SocialLoginFailed({
              src: 'UserLoginComponent',
              description: 'Facebook Login action failed',
            }),
          );
        }
      });
  }
  //Facebook Sign in Button Trigger
  async facebookLoginActionCap() {
    this.messageService.sendMessage('facebookLogin');
  }
  //Google Sign in Button Trigger
  triggerGoogleSignIn() {
    this.messageService.sendMessage('googleSignIn');
  }
  //Apple Sign in Button Trigger
  triggerAppleSignIn() {
    this.messageService.sendMessage('appleSignIn');
  }
  //Apple Sign in For Web
  async signInWithAppleWeb() {
    const redirectURI = window.location.href.split('?')[0];
    AppleID.auth.init({
      clientId: envConst.appleClientId,
      scope: 'name email',
      redirectURI,
      state: 'origin:web',
      usePopup: true,
    });

    let response;
    try {
      response = await AppleID.auth.signIn();
    } catch (err) {
      console.error(err);
      this.logger.error(
        `onSocialLogin error during sign-in with provider Apple`,
        err,
      );
    }
    if (
      response &&
      response.authorization &&
      response.authorization.state === 'origin:web' &&
      response.authorization.id_token &&
      response.authorization.code
    ) {
      const appleToken = this.jwtHelper.decodeToken(
        response.authorization.id_token,
      );
      const userName = response.user ? response.user : {};
      const socialAccountDetails: SocialAccountLoginDetails = {
        provider: 'apple',
        accessToken: response.authorization.id_token,
        appleCode: response.authorization.code,
        email: appleToken.email,
        firstName: userName?.name?.firstName,
        lastName: userName?.name?.lastName,
      };
      socialAccountDetails.mode = this.mode;
      window.parent.postMessage(socialAccountDetails, document.referrer);
    } else if (response && response.error) {
      console.error(response.error);
      this.logger.error(
        `onSocialLogin error during sign-in with provider Apple`,
        response.error,
      );
      if (this.authType === 'login') {
        this.store.dispatch(
          new SocialLoginFailed({
            src: 'UserLoginComponent',
            description: 'Apple Login action failed',
          }),
        );
      }
    }
  }

  onLocaleChange($event) {
    this.translate.use($event.locale);
  }

  logout() {
    this.authService
      .signOut(false)
      .then(() => console.log('Logged out Successfully !!'))
      .catch((err) => console.error(err));
  }
}
